import { getPlanDisplayName, legacyPlanToPlanId, resolvePlanId } from '../config/entitlements';

const DAY_MS = 24 * 60 * 60 * 1000;

export function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatLongDate(value) {
  const date = toDate(value);
  if (!date) return 'Not available';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function getTimeRemaining(expiryValue, active) {
  const expiryDate = toDate(expiryValue);
  if (!expiryDate) {
    return {
      label: active ? 'Active access confirmed' : 'No expiry date available',
      tone: active ? 'positive' : 'muted',
    };
  }

  const diffMs = expiryDate.getTime() - Date.now();
  const dayDiff = Math.ceil(diffMs / DAY_MS);

  if (active) {
    if (dayDiff <= 0) {
      return { label: 'Expires today', tone: 'warning' };
    }
    if (dayDiff === 1) {
      return { label: '1 day left', tone: 'warning' };
    }
    return { label: `${dayDiff} days left`, tone: dayDiff <= 7 ? 'warning' : 'positive' };
  }

  if (dayDiff === 0) {
    return { label: 'Expired today', tone: 'critical' };
  }

  const daysAgo = Math.max(1, Math.abs(dayDiff));
  return {
    label: `Expired ${daysAgo} day${daysAgo === 1 ? '' : 's'} ago`,
    tone: 'critical',
  };
}

function getStatusTone(status) {
  switch (status) {
    case 'active':
      return 'positive';
    case 'expired':
      return 'critical';
    case 'verification_unavailable':
      return 'warning';
    default:
      return 'muted';
  }
}

function getStatusLabel(status) {
  switch (status) {
    case 'active':
      return 'Active';
    case 'expired':
      return 'Expired';
    case 'verification_unavailable':
      return 'Verification unavailable';
    case 'no_subscription':
    default:
      return 'No active subscription';
  }
}

function getSyncMessage({ syncState, active, hasPlan, firestoreError, entitlementError }) {
  if (syncState === 'healthy') {
    if (active) return 'Your account access matches current backend enforcement.';
    if (hasPlan) return 'Your subscription is on file, but premium access is not currently active.';
    return 'You do not currently have an active premium subscription.';
  }

  if (syncState === 'degraded') {
    if (entitlementError) {
      return 'We could not verify live access right now. Displayed dates are based on your latest subscription record.';
    }
    if (firestoreError) {
      return 'We confirmed your current access, but live subscription metadata is temporarily unavailable.';
    }
  }

  return 'Subscription details are temporarily unavailable. Please retry in a moment.';
}

function inferTrialTrack(promoEntitlements) {
  const list = Array.isArray(promoEntitlements) ? promoEntitlements : [];
  const hasMckinseyTrial =
    list.includes('sea_wolf_simulation') ||
    list.includes('red_rock_simulation') ||
    list.includes('sea_wolf_solver');
  const hasBcgTrial = list.includes('casey_simulation');

  if (hasMckinseyTrial && hasBcgTrial) return 'McKinsey + BCG';
  if (hasMckinseyTrial) return 'McKinsey';
  if (hasBcgTrial) return 'BCG';
  return null;
}

export function normalizeAccountSubscription({
  firestoreData,
  firestoreError,
  entitlementData,
  entitlementError,
  userCreationTime,
}) {
  const backendPlanId = entitlementData?.plan_id ?? null;
  const firestorePlanId = resolvePlanId(firestoreData?.plan_id, firestoreData?.plan);
  const planId = backendPlanId ?? firestorePlanId ?? null;
  const hasPlan = planId != null;

  const signUpDate = toDate(firestoreData?.signUpDate) ?? toDate(userCreationTime);
  const expiryDate =
    toDate(firestoreData?.expiryDate) ??
    toDate(entitlementData?.current_period_end) ??
    null;
  const billingTerm = entitlementData?.billing_term ?? firestoreData?.billing_term ?? null;

  const hasEntitlementTruth = entitlementData && !entitlementError;
  const hasFirestoreMetadata = !!firestoreData;
  const promoEntitlements = Array.isArray(entitlementData?.promo_entitlements)
    ? entitlementData.promo_entitlements.filter((item) => typeof item === 'string')
    : [];
  const promoTrialActive = Boolean(entitlementData?.promo_active) || promoEntitlements.length > 0;
  const trialTrack = inferTrialTrack(promoEntitlements);

  let status = 'no_subscription';
  let active = false;
  let syncState = 'unavailable';

  if (hasEntitlementTruth) {
    active = entitlementData.active === true;
    status = active ? 'active' : hasPlan ? 'expired' : 'no_subscription';
    syncState = hasFirestoreMetadata || !firestoreError ? 'healthy' : 'degraded';
  } else if (hasFirestoreMetadata) {
    status = hasPlan ? 'verification_unavailable' : 'no_subscription';
    active = false;
    syncState = 'degraded';
  } else if (firestoreError || entitlementError) {
    status = hasPlan ? 'verification_unavailable' : 'no_subscription';
    active = false;
    syncState = 'unavailable';
  }

  const timeRemaining = getTimeRemaining(expiryDate, active);
  const planLabel =
    planId != null ? getPlanDisplayName(planId) : getPlanDisplayName(legacyPlanToPlanId(firestoreData?.plan)) || 'Free';

  return {
    planId,
    planLabel: planLabel || 'Free',
    status,
    statusLabel: getStatusLabel(status),
    statusTone: getStatusTone(status),
    active,
    billingTerm,
    signUpDate,
    expiryDate,
    timeRemainingLabel: timeRemaining.label,
    timeRemainingTone: timeRemaining.tone,
    syncState,
    syncMessage: getSyncMessage({
      syncState,
      active,
      hasPlan,
      firestoreError,
      entitlementError,
    }),
    canUpgrade: planId !== 'all_access',
    rawFirestoreStatus: firestoreData?.status ?? null,
    promoTrialActive,
    trialTrack,
    trialLabel: promoTrialActive ? '14-day trial active' : 'No active trial',
  };
}
